import { GROUP_DISPLAY_NAMES } from '../constants';

export class ValidationService {
    static validateScheduleCollection(allSchedulesCollection, validationErrors, requiredFieldsGetter) {
        const realSchedules = this.filterRealSchedules(allSchedulesCollection);

        if (!realSchedules || realSchedules.length === 0) {
            return {
                isValid: false,
                message: 'Please add at least one property to complete the schedule.',
                missingFieldsByGroup: {},
                totalMissingFields: 0
            };
        }

        const allRequiredFields = this.getAllRequiredFieldsForAllGroups(requiredFieldsGetter);
        const { missingFieldsByGroup, totalMissingFields } = this.validateRequiredFields(
            realSchedules,
            allRequiredFields
        );

        const hasValidationErrors = Object.keys(validationErrors).length > 0;
        const hasEmptyFields = totalMissingFields > 0;

        return {
            isValid: !hasEmptyFields && !hasValidationErrors,
            message: this.getValidationMessage(hasValidationErrors, hasEmptyFields),
            missingFieldsByGroup,
            totalMissingFields,
            hasValidationErrors
        };
    }

    static filterRealSchedules(schedules) {
        return schedules.filter(schedule =>
            schedule.id &&
            schedule.id !== "" &&
            !schedule._new &&
            !schedule._emptyRowId &&
            this.hasNonEmptyValues(schedule)
        );
    }

    static hasNonEmptyValues(schedule) {
        return Object.keys(schedule).some(key =>
            !['_new', '_emptyRowId', 'key'].includes(key) &&
            schedule[key] &&
            schedule[key].toString().trim() !== ''
        );
    }

    static validateRequiredFields(schedules, allRequiredFields) {
        const missingFieldsByGroup = {};
        let totalMissingFields = 0;

        const groupedSchedules = this.groupSchedulesByType(schedules);

        Object.keys(groupedSchedules).forEach(groupType => {
            const groupSchedules = groupedSchedules[groupType];
            const requiredFieldsForGroup = allRequiredFields[groupType] || [];

            missingFieldsByGroup[groupType] = [];

            groupSchedules.forEach((schedule, index) => {
                requiredFieldsForGroup.forEach(field => {
                    const value = schedule[field];
                    if (!value || value.toString().trim() === '') {
                        missingFieldsByGroup[groupType].push({
                            rowIndex: index + 1,
                            field: field,
                            id: schedule.id
                        });
                        totalMissingFields++;
                    }
                });
            });
        });

        return { missingFieldsByGroup, totalMissingFields };
    }

    static groupSchedulesByType(schedules) {
        return schedules.reduce((acc, schedule) => {
            const scheduleType = schedule.group;
            if (!scheduleType) return acc;

            if (!acc[scheduleType]) {
                acc[scheduleType] = [];
            }
            acc[scheduleType].push(schedule);
            return acc;
        }, {});
    }

    static getAllRequiredFieldsForAllGroups(requiredFieldsGetter) {
        const allGroups = ['fix_and_flips', 'investment_properties_owned', 'primary_residence'];
        const allRequiredFields = {};

        allGroups.forEach(groupName => {
            allRequiredFields[groupName] = requiredFieldsGetter(groupName);
        });

        return allRequiredFields;
    }

    static getValidationMessage(hasValidationErrors, hasEmptyFields) {
        if (hasValidationErrors) {
            return 'Please fix validation errors before submitting.';
        }
        if (hasEmptyFields) {
            return 'All required fields must be completed before proceeding.';
        }
        return null;
    }

    static getDetailedValidationMessage(validationResult, allSchedulesCollection) {
        if (!validationResult.isValid) {
            if (!allSchedulesCollection || allSchedulesCollection.length === 0) {
                return 'Please add at least one property to complete the schedule.';
            }

            if (validationResult.hasValidationErrors) {
                return 'Please fix validation errors before submitting.';
            }

            if (validationResult.totalMissingFields > 0) {
                const groupMessages = this.createGroupMessages(validationResult.missingFieldsByGroup);
                return `All required fields must be completed before proceeding.`;
            }
        }

        return null;
    }

    static createGroupMessages(missingFieldsByGroup) {
        return Object.keys(missingFieldsByGroup)
            .filter(groupType => missingFieldsByGroup[groupType].length > 0)
            .map(groupType => {
                const missingFields = missingFieldsByGroup[groupType];
                const groupDisplayName = this.getGroupDisplayName(groupType);
                const fieldCount = missingFields.length;
                return `${groupDisplayName}: ${fieldCount} field${fieldCount > 1 ? 's' : ''} missing`;
            });
    }

    static getGroupDisplayName(groupType) {
        return GROUP_DISPLAY_NAMES[groupType] || groupType;
    }

    static createMissingFieldsMap(validationResult) {
        const missingFieldsMap = {};

        if (validationResult.missingFieldsByGroup) {
            Object.keys(validationResult.missingFieldsByGroup).forEach(groupType => {
                const missingFields = validationResult.missingFieldsByGroup[groupType];
                missingFields.forEach(field => {
                    const key = `${field.id}_${field.field}`;
                    missingFieldsMap[key] = true;
                });
            });
        }

        return missingFieldsMap;
    }
}
